/**
 * Safe localStorage wrapper that handles SecurityError exceptions
 * Some browsers (incognito mode, strict privacy settings) throw SecurityError when accessing localStorage
 */

/**
 * Checks if localStorage is available and accessible
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely get an item from localStorage
 * @returns The value or null if not found or localStorage is unavailable
 */
export const safeLocalStorageGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    if (error instanceof Error && error.name === 'SecurityError') {
      console.warn('localStorage access denied:', error.message);
    }
    return null;
  }
};

/**
 * Safely set an item in localStorage
 * @returns true if successful, false if localStorage is unavailable
 */
export const safeLocalStorageSetItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (error instanceof Error && error.name === 'SecurityError') {
      console.warn('localStorage access denied:', error.message);
    }
    return false;
  }
};

/**
 * Safely remove an item from localStorage
 * @returns true if successful, false if localStorage is unavailable
 */
export const safeLocalStorageRemoveItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    if (error instanceof Error && error.name === 'SecurityError') {
      console.warn('localStorage access denied:', error.message);
    }
    return false;
  }
};

/**
 * Safely clear all items from localStorage
 * @returns true if successful, false if localStorage is unavailable
 */
export const safeLocalStorageClear = (): boolean => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    if (error instanceof Error && error.name === 'SecurityError') {
      console.warn('localStorage access denied:', error.message);
    }
    return false;
  }
};
